import { TextField, Box, Button, Paper, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { createProductThunk, getProductsThunk } from '@redux/features/product.slice';
import { getCategoriesThunk, getCategoryWithSubByIdThunk } from '@redux/features/category.slice';
import { getDosagesThunk } from '@redux/features/dosage.slice';
import { getManufacturersThunk } from '@redux/features/manufacturer.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useMemo, useEffect, useState } from 'react';
import * as Yup from 'yup';

interface IProps {
    show: boolean;
    handleClose: () => void;
}

const AddProductModal: FC<IProps> = ({ show, handleClose }) => {
    const { loading } = useAppSelector((state: RootState) => state.productState);
    const { categories } = useAppSelector((state: RootState) => state.categoryState);
    const { dosages } = useAppSelector((state: RootState) => state.dosageState);
    const { manufacturers } = useAppSelector((state: RootState) => state.manufacturerState);
    const dispatch = useAppDispatch();
    const [selectedSubCategories, setSelectedSubCategories] = useState<any[]>([]);

    // Fetch dropdown data when modal opens
    useEffect(() => {
        if (show) {
            dispatch(getCategoriesThunk());
            dispatch(getDosagesThunk());
            dispatch(getManufacturersThunk());
        } else {
            // Reset subcategories when modal closes
            setSelectedSubCategories([]);
        }
    }, [show, dispatch]);

    const validationSchema = Yup.object({
        itemReference: Yup.string(),
        itemName: Yup.string().required('Product name is required'),
        unitPrice: Yup.number().required('Unit price is required').min(0, 'Must be >= 0'),
        priceOffer: Yup.number().required('Price offer is required').min(0, 'Must be >= 0'),
        brandName: Yup.string().required('Brand name is required'),
        brandGeneric: Yup.string().required('Brand generic is required'),
        ndcCode: Yup.string().required('NDC code is required'),
        barcode: Yup.string().required('Barcode is required'),
        strength: Yup.string().required('Strength is required'),
        packSize: Yup.string().required('Pack size is required'),
        // companyPrefix and itemReference are no longer strictly required for creation
        // they will be populated from the first EPCIS inbound import
        companyPrefix: Yup.string(),

        packagingUom: Yup.string().required('Packaging UOM is required'),
        wholesaleUom: Yup.string().required('Wholesale UOM is required'),
        storageCondition: Yup.string().required('Storage condition is required'),
        locationInWarehouse: Yup.string().required('Location in warehouse is required'),
        additionalInfo: Yup.string().required('Additional info is required'),
        reorderLevel: Yup.number().required('Reorder level is required').min(0, 'Must be >= 0'),
        stockLevel: Yup.number().required('Stock level is required').min(0, 'Must be >= 0'),
        categoryId: Yup.number().required('Category is required').min(1, 'Please select a category'),
        subCategoriesId: Yup.number().required('Sub category is required').min(1, 'Please select a sub category'),
        dosageId: Yup.number().required('Dosage is required').min(1, 'Please select a dosage'),
        manufacturersId: Yup.number().required('Manufacturer is required').min(1, 'Please select a manufacturer'),
        gtin: Yup.string().required('GTIN is required'),
        photoFile: Yup.mixed().required('Photo file is required'),
    });

    const handleSubmit = (values: any) => {
        const body: any = {
            // Minimal required fields + sensible defaults to satisfy API
            itemName: values.itemName,
            itemReference: values.itemReference,
            unitPrice: Number(values.unitPrice),
            priceOffer: Number(values.priceOffer),
            brandName: values.brandName,
            brandGeneric: values.brandGeneric,
            ndcCode: values.ndcCode,
            barcode: values.barcode,
            strength: values.strength,
            packSize: values.packSize,
            companyPrefix: values.companyPrefix,
            packagingUom: values.packagingUom,
            wholesaleUom: values.wholesaleUom,
            storageCondition: values.storageCondition,
            locationInWarehouse: values.locationInWarehouse,
            additionalInfo: values.additionalInfo,
            reorderLevel: Number(values.reorderLevel || 0),
            stockLevel: Number(values.stockLevel || 0),
            categoryId: Number(values.categoryId || 0),
            subCategoriesId: Number(values.subCategoriesId || 0),
            dosageId: Number(values.dosageId || 0),
            manufacturersId: Number(values.manufacturersId || 0),
            gtin: values.gtin,
            photoFile: values.photoFile,
        };
        dispatch(createProductThunk(body)).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                handleClose();
                formik.resetForm();
                dispatch(getProductsThunk({ page: 0, productSize: 10 }));
            }
        });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            itemReference: '',
            itemName: '',
            unitPrice: 0,
            priceOffer: 0,
            brandName: '',
            brandGeneric: '',
            ndcCode: '',
            barcode: '',
            strength: '',
            packSize: '',
            companyPrefix: '',
            packagingUom: '',
            wholesaleUom: '',
            storageCondition: '',
            locationInWarehouse: '',
            additionalInfo: '',
            reorderLevel: 0,
            stockLevel: 0,
            categoryId: 0,
            subCategoriesId: 0,
            dosageId: 0,
            manufacturersId: 0,
            gtin: '',
            photoFile: undefined as unknown as File,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    const previews = useMemo(() => {
        const toUrl = (f?: File) => (f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
        return {
            photoFile: toUrl(formik.values.photoFile),
        } as Record<string, string | null>;
    }, [formik.values.photoFile]);

    // Fetch subcategories when category is selected
    useEffect(() => {
        const categoryId = formik.values.categoryId;
        if (categoryId && categoryId !== 0) {
            dispatch(getCategoryWithSubByIdThunk(categoryId)).then((res: any) => {
                if (res.meta.requestStatus === 'fulfilled' && res.payload?.Category?.subCategories) {
                    setSelectedSubCategories(res.payload.Category.subCategories);
                } else {
                    setSelectedSubCategories([]);
                }
            });
            // Reset subcategory when category changes
            formik.setFieldValue('subCategoriesId', 0);
        } else {
            setSelectedSubCategories([]);
            formik.setFieldValue('subCategoriesId', 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.categoryId, dispatch]);

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white shadow rounded-md overflow-hidden md:w-[1000px] md:h-[90vh] flex flex-col"
        >
            <Modal.Header className="p-3 flex justify-between items-center">
                <Modal.Title className="text-lg font-semibold">Add Product</Modal.Title>
            </Modal.Header>
            <Modal.Body className="flex-1 overflow-y-auto p-4">
                <form onSubmit={formik.handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField
                        size="small"
                        fullWidth
                        id="itemName"
                        name="itemName"
                        label="Product Name"
                        value={formik.values.itemName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.itemName && Boolean(formik.errors.itemName)}
                        helperText={formik.touched.itemName && formik.errors.itemName}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        disabled={true}
                        id="itemReference"
                        name="itemReference"
                        label="Item Reference (Auto-populated from EPCIS)"
                        value={formik.values.itemReference}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.itemReference && Boolean(formik.errors.itemReference)}
                        helperText={formik.touched.itemReference && formik.errors.itemReference}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        id="unitPrice"
                        name="unitPrice"
                        label="Unit Price"
                        value={formik.values.unitPrice}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.unitPrice && Boolean(formik.errors.unitPrice)}
                        helperText={formik.touched.unitPrice && formik.errors.unitPrice}
                        inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        id="priceOffer"
                        name="priceOffer"
                        label="Price Offer"
                        value={formik.values.priceOffer}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.priceOffer && Boolean(formik.errors.priceOffer)}
                        helperText={formik.touched.priceOffer && formik.errors.priceOffer}
                        inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="brandName"
                        name="brandName"
                        label="Brand Name"
                        value={formik.values.brandName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.brandName && Boolean(formik.errors.brandName)}
                        helperText={formik.touched.brandName && formik.errors.brandName}
                    />

                    <FormControl
                        fullWidth
                        size="small"
                        error={formik.touched.brandGeneric && Boolean(formik.errors.brandGeneric)}
                    >
                        <InputLabel id="brandGeneric-label">Brand Generic</InputLabel>
                        <Select
                            labelId="brandGeneric-label"
                            id="brandGeneric"
                            name="brandGeneric"
                            label="Brand Generic"
                            value={formik.values.brandGeneric || ''}
                            onChange={(e) => formik.setFieldValue('brandGeneric', e.target.value)}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem value="Brand">Brand</MenuItem>
                            <MenuItem value="Generic">Generic</MenuItem>
                        </Select>
                        {formik.touched.brandGeneric && formik.errors.brandGeneric && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                {formik.errors.brandGeneric}
                            </Typography>
                        )}
                    </FormControl>
                    <TextField
                        size="small"
                        fullWidth
                        id="ndcCode"
                        name="ndcCode"
                        label="NDC Code"
                        value={formik.values.ndcCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.ndcCode && Boolean(formik.errors.ndcCode)}
                        helperText={formik.touched.ndcCode && formik.errors.ndcCode}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="barcode"
                        name="barcode"
                        label="Barcode"
                        value={formik.values.barcode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.barcode && Boolean(formik.errors.barcode)}
                        helperText={formik.touched.barcode && formik.errors.barcode}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="strength"
                        name="strength"
                        label="Strength"
                        value={formik.values.strength}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.strength && Boolean(formik.errors.strength)}
                        helperText={formik.touched.strength && formik.errors.strength}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="packSize"
                        name="packSize"
                        label="Pack Size"
                        value={formik.values.packSize}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.packSize && Boolean(formik.errors.packSize)}
                        helperText={formik.touched.packSize && formik.errors.packSize}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        disabled={true}
                        id="companyPrefix"
                        name="companyPrefix"
                        label="Company Prefix (Auto-populated from EPCIS)"
                        value={formik.values.companyPrefix}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.companyPrefix && Boolean(formik.errors.companyPrefix)}
                        helperText={formik.touched.companyPrefix && formik.errors.companyPrefix}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="packagingUom"
                        name="packagingUom"
                        label="Packaging UOM"
                        value={formik.values.packagingUom}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.packagingUom && Boolean(formik.errors.packagingUom)}
                        helperText={formik.touched.packagingUom && formik.errors.packagingUom}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="wholesaleUom"
                        name="wholesaleUom"
                        label="Wholesale UOM"
                        value={formik.values.wholesaleUom}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.wholesaleUom && Boolean(formik.errors.wholesaleUom)}
                        helperText={formik.touched.wholesaleUom && formik.errors.wholesaleUom}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="storageCondition"
                        name="storageCondition"
                        label="Storage Condition"
                        value={formik.values.storageCondition}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.storageCondition && Boolean(formik.errors.storageCondition)}
                        helperText={formik.touched.storageCondition && formik.errors.storageCondition}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="locationInWarehouse"
                        name="locationInWarehouse"
                        label="Location in Warehouse"
                        value={formik.values.locationInWarehouse}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.locationInWarehouse && Boolean(formik.errors.locationInWarehouse)}
                        helperText={formik.touched.locationInWarehouse && formik.errors.locationInWarehouse}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="additionalInfo"
                        name="additionalInfo"
                        label="Additional Info"
                        value={formik.values.additionalInfo}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.additionalInfo && Boolean(formik.errors.additionalInfo)}
                        helperText={formik.touched.additionalInfo && formik.errors.additionalInfo}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        id="reorderLevel"
                        name="reorderLevel"
                        label="Reorder Level"
                        value={formik.values.reorderLevel}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.reorderLevel && Boolean(formik.errors.reorderLevel)}
                        helperText={formik.touched.reorderLevel && formik.errors.reorderLevel}
                        inputProps={{ min: 0 }}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        id="stockLevel"
                        name="stockLevel"
                        label="Stock Level"
                        value={formik.values.stockLevel}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.stockLevel && Boolean(formik.errors.stockLevel)}
                        helperText={formik.touched.stockLevel && formik.errors.stockLevel}
                        inputProps={{ min: 0 }}
                    />
                    <FormControl
                        fullWidth
                        size="small"
                        error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                    >
                        <InputLabel id="categoryId-label">Category</InputLabel>
                        <Select
                            labelId="categoryId-label"
                            id="categoryId"
                            name="categoryId"
                            label="Category"
                            value={formik.values.categoryId ?? 0}
                            onChange={(e) => formik.setFieldValue('categoryId', Number(e.target.value))}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem value={0}>None</MenuItem>
                            {categories?.map((category: { id: number; categoryName: string }) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.categoryName}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.categoryId && formik.errors.categoryId && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                {formik.errors.categoryId}
                            </Typography>
                        )}
                    </FormControl>
                    <FormControl
                        fullWidth
                        size="small"
                        error={formik.touched.subCategoriesId && Boolean(formik.errors.subCategoriesId)}
                    >
                        <InputLabel id="subCategoriesId-label">Sub Category</InputLabel>
                        <Select
                            labelId="subCategoriesId-label"
                            id="subCategoriesId"
                            name="subCategoriesId"
                            label="Sub Category"
                            value={formik.values.subCategoriesId ?? 0}
                            onChange={(e) => formik.setFieldValue('subCategoriesId', Number(e.target.value))}
                            onBlur={formik.handleBlur}
                            disabled={
                                !formik.values.categoryId ||
                                formik.values.categoryId === 0 ||
                                selectedSubCategories.length === 0
                            }
                        >
                            <MenuItem value={0}>None</MenuItem>
                            {selectedSubCategories?.map((subCategory: { id: number; categoryName: string }) => (
                                <MenuItem key={subCategory.id} value={subCategory.id}>
                                    {subCategory.categoryName}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.subCategoriesId && formik.errors.subCategoriesId && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                {formik.errors.subCategoriesId}
                            </Typography>
                        )}
                    </FormControl>
                    <FormControl
                        fullWidth
                        size="small"
                        error={formik.touched.dosageId && Boolean(formik.errors.dosageId)}
                    >
                        <InputLabel id="dosageId-label">Dosage</InputLabel>
                        <Select
                            labelId="dosageId-label"
                            id="dosageId"
                            name="dosageId"
                            label="Dosage"
                            value={formik.values.dosageId ?? 0}
                            onChange={(e) => formik.setFieldValue('dosageId', Number(e.target.value))}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem value={0}>None</MenuItem>
                            {dosages?.map((dosage: { id: number; dosageName: string }) => (
                                <MenuItem key={dosage.id} value={dosage.id}>
                                    {dosage.dosageName}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.dosageId && formik.errors.dosageId && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                {formik.errors.dosageId}
                            </Typography>
                        )}
                    </FormControl>
                    <FormControl
                        fullWidth
                        size="small"
                        error={formik.touched.manufacturersId && Boolean(formik.errors.manufacturersId)}
                    >
                        <InputLabel id="manufacturersId-label">Manufacturer</InputLabel>
                        <Select
                            labelId="manufacturersId-label"
                            id="manufacturersId"
                            name="manufacturersId"
                            label="Manufacturer"
                            value={formik.values.manufacturersId ?? 0}
                            onChange={(e) => formik.setFieldValue('manufacturersId', Number(e.target.value))}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem value={0}>None</MenuItem>
                            {manufacturers?.map((manufacturer: { id: number; name: string }) => (
                                <MenuItem key={manufacturer.id} value={manufacturer.id}>
                                    {manufacturer.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.manufacturersId && formik.errors.manufacturersId && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                {formik.errors.manufacturersId}
                            </Typography>
                        )}
                    </FormControl>
                    <TextField
                        size="small"
                        fullWidth
                        id="gtin"
                        name="gtin"
                        label="GTIN"
                        value={formik.values.gtin}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.gtin && Boolean(formik.errors.gtin)}
                        helperText={formik.touched.gtin && formik.errors.gtin}
                    />
                    <div className="col-span-full">
                        <Paper
                            variant="outlined"
                            className={`p-3 flex items-center gap-3 ${formik.touched.photoFile && formik.errors.photoFile ? 'border-red-500' : ''}`}
                        >
                            <Box className="flex-1">
                                <Typography variant="body2" className="mb-1 text-slate-700">
                                    Photo File <span className="text-red-500">*</span>
                                </Typography>
                                {(formik.values as any).photoFile ? (
                                    <Box className="flex items-center gap-3">
                                        {(previews as any).photoFile ? (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img
                                                src={(previews as any).photoFile}
                                                alt="preview"
                                                className="w-12 h-12 rounded object-cover border"
                                            />
                                        ) : (
                                            <Box className="w-12 h-12 rounded border flex items-center justify-center text-xs text-slate-500">
                                                File
                                            </Box>
                                        )}
                                        <Box className="min-w-0">
                                            <Typography variant="body2" className="truncate max-w-[220px]">
                                                {((formik.values as any).photoFile as File).name}
                                            </Typography>
                                            <Typography variant="caption" className="text-slate-500">
                                                {(((formik.values as any).photoFile as File).size / 1024).toFixed(1)} KB
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography variant="caption" className="text-slate-500">
                                        No file selected
                                    </Typography>
                                )}
                            </Box>
                            <Box className="flex items-center gap-2">
                                <Button variant="outlined" size="small" component="label">
                                    Upload
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*,.pdf,.doc,.docx,.png,.jpg,.jpeg"
                                        onChange={(e) =>
                                            (formik as any).setFieldValue('photoFile', e.currentTarget.files?.[0])
                                        }
                                    />
                                </Button>
                                {(formik.values as any).photoFile && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => (formik as any).setFieldValue('photoFile', undefined)}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                        {formik.touched.photoFile && formik.errors.photoFile && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                {(formik.errors.photoFile as any)?.toString() || 'Photo file is required'}
                            </Typography>
                        )}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer className="sticky bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex items-center justify-center gap-2">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={loading}
                />
                <ButtonBase
                    innerText="Add Product"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={loading}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default AddProductModal;
